import styled from 'styled-components';
import { Page as StyledPage } from 'react-pdf';
import { ButtonStyles, BREAKPOINTS } from '@livingsecurity/shared';

// Main slideshow section - full viewport
export const SlideshowSection = styled.section`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Skip link for accessibility
export const SkipLink = styled.button`
  position: absolute;
  top: -40px;
  left: 6px;
  background: var(--dark-blue-two);
  color: white;
  padding: 8px;
  text-decoration: none;
  border: none;
  border-radius: 4px;
  z-index: 1000;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    top: 6px;
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }
`;

// Slideshow header - fixed height
export const SlideshowHeader = styled.header`
  flex-shrink: 0;
  background: white;
  border-bottom: 1px solid #e0e0e0;
`;

// Slideshow title
export const SlideshowTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #000;
  margin: 0 0 8px 0;
  padding: 16px 24px 0;
  
  @media (max-width: ${BREAKPOINTS.PHONE}) {
    font-size: 18px;
    padding: 12px 16px 0;
  }
`;

// Navigation section - fixed height
export const SlideshowNav = styled.nav`
  flex-shrink: 0;
  background: white;
`;

// Navigation container
export const NavigationContainer = styled.div`
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
  gap: 16px;

  ${ButtonStyles.ButtonWrapper} {
    font-size: 16px;
    font-weight: bold;
    min-width: 120px;
    height: 48px;
    text-transform: uppercase;
    background-color: var(--poppy);
  }

  @media (max-width: ${BREAKPOINTS.PHONE}) {
    padding: 12px 16px;
    gap: 8px;
    
    ${ButtonStyles.ButtonWrapper} {
      font-size: 14px;
      min-width: 80px;
      height: 36px;
    }
  }
`;

// Page indicator
export const PageIndicator = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-align: center;
  min-width: 100px;
  
  @media (max-width: ${BREAKPOINTS.PHONE}) {
    font-size: 14px;
    min-width: 80px;
  }
`;

// Main content area - takes remaining height
export const SlideshowMain = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
`;

export const Page = styled(StyledPage)`
  canvas {
    height: calc(100dvh - 200px) !important;
    width: 100% !important;
    max-width: 100vw;
    object-fit: contain;
  }

  @media (max-width: ${BREAKPOINTS.PHONE}) {
    min-width: 420px;
    width: 420px !important;
    margin: 0;
    overflow-x: auto;

    canvas {
      min-width: 420px;
      width: 420px !important;
      height: auto !important;
    }
  }

  @media (max-height: 500px) and (orientation: landscape) {
    min-width: 100vw;
    width: 100vw !important;
    height: 100dvh !important;
    margin: 0;
    overflow-x: auto;

    canvas {
      height: auto !important;
      min-width: 100vw;
      width: 100vw !important;
    }
  }
`;

export const SlideshowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  background: white;
  flex: 1;
  
  /* Focus styles for accessibility */
  &:focus {
    outline: 2px solid #0066cc;
    outline-offset: 2px;
  }
  
  &:focus:not(:focus-visible) {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid #0066cc;
    outline-offset: 2px;
  }

  @media (max-width: ${BREAKPOINTS.PHONE}), (max-height: ${BREAKPOINTS.PHONE}) and (orientation: landscape) {
    margin: 0;
    overflow-x: auto;
  }
`;

// Footer section - fixed height
export const SlideshowFooter = styled.footer`
  flex-shrink: 0;
  background: white;
`;

export const NotificationContainer = styled.div`
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 24px;
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  width: 100%;

  ${ButtonStyles.ButtonWrapper} {
    font-size: 18px;
    font-weight: bold;
    min-width: 160px;
    height: 48px;
    text-transform: uppercase;
    background-color: var(--poppy);
  }

  @media (max-width: ${BREAKPOINTS.PHONE}), (max-height: ${BREAKPOINTS.PHONE}) and (orientation: landscape) {
    padding: 12px 16px;
    
    ${ButtonStyles.ButtonWrapper} {
      font-size: 14px;
      min-width: 120px;
      height: 36px;
    }
  }
`;

// Legacy components - keeping for backward compatibility
export const NotificationInfo = styled.div`
  color: #000000;
  margin-right: 32px;
`;

export const NotificationNextContent = styled.div`
  font-size: 24px;
`;
